import { useState, useEffect } from "react";
import { FileText, RefreshCw, FileDown, Loader2 } from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import { useCompanyContext } from "../../../context";
import { getReports, getAvailableTargetTypesAB } from "../../../api/wiki";
import TileReport from "../Wiki/tiles/TileReport";
import ReportPDF, { CONFIG, buildFileName } from "../Wiki/tiles/ReportPDF";

const isAB = process.env.REACT_APP_IS_AB;

/* ── Theme ── */

const THEMES = {
  dark: {
    badgeBg: "rgba(14,165,233,0.10)",
    badgeBorder: "rgba(14,165,233,0.22)",
    badgeText: "#38bdf8",
    labelBg: "rgba(255,255,255,0.06)",
    labelBorder: "rgba(255,255,255,0.10)",
    labelText: "#a1a1aa",
    textSecondary: "#d4d4d8",
    textMuted: "#71717a",
    divider: "rgba(255,255,255,0.08)",
    pageBg: "#121C2E",
    cardBorder: "rgba(255,255,255,0.08)",
    accent: "#0EA5E9",
  },
  light: {
    badgeBg: "rgba(14,165,233,0.08)",
    badgeBorder: "rgba(14,165,233,0.20)",
    badgeText: "#0284c7",
    labelBg: "rgba(0,0,0,0.04)",
    labelBorder: "rgba(0,0,0,0.10)",
    labelText: "#71717a",
    textSecondary: "#3f3f46",
    textMuted: "#a1a1aa",
    divider: "rgba(0,0,0,0.08)",
    pageBg: "#e4eaf2",
    cardBorder: "rgba(0,0,0,0.08)",
    accent: "#2d3d52",
  },
};

const useReportTheme = () => {
  const [dark, setDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setDark(document.documentElement.classList.contains("dark")),
    );
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => obs.disconnect();
  }, []);
  return { ...(dark ? THEMES.dark : THEMES.light), isDark: dark };
};

/* ── Skeleton ── */

const TileSkeleton = ({ delay = 0 }) => (
  <div
    className="rounded-xl border border-surfaceBorder bg-reortTileBg overflow-hidden"
    style={{ minHeight: 160, animationDelay: `${delay}ms` }}
  >
    <div className="px-5 pt-4 pb-3">
      <div className="flex items-center justify-between mb-3">
        <div className="h-4 w-2/5 rounded-md bg-innerBg animate-pulse" />
      </div>
      <div className="flex gap-2">
        <div className="h-5 w-20 rounded-md bg-innerBg animate-pulse" />
        <div className="h-5 w-14 rounded bg-innerBg animate-pulse" />
      </div>
    </div>
    <div className="h-px mx-5 bg-innerBg" />
    <div className="px-5 pt-3 pb-4 flex flex-col gap-2.5">
      <div className="h-3 w-full rounded bg-innerBg animate-pulse" />
      <div className="h-3 w-5/6 rounded bg-innerBg animate-pulse" />
      <div className="h-3 w-4/6 rounded bg-innerBg animate-pulse" />
    </div>
  </div>
);

/* ── DefaultReportView ── */

const DefaultReportView = () => {
  const { currentCompany } = useCompanyContext();
  const t = useReportTheme();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const companyName = currentCompany?.case || "";
  const target = currentCompany?.target || "";

  const loadReports = () => {
    setLoading(true);
    setError(false);
    setVisible(false);
    getReports(currentCompany?.target || "")
      .then((res) => {
        console.log({ res });
        setReports(res?.data || []);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => setVisible(true)),
        );
      })
      .catch(() => {
        setReports([]);
        setError(true);
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isAB) getAvailableTargetTypesAB(target);
    console.log("load them");
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const handleExportAll = async () => {
    if (isExporting || reports.length === 0) return;
    setIsExporting(true);
    try {
      const blob = await pdf(
        <ReportPDF reports={reports} companyName={companyName} />,
      ).toBlob();

      const today = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = buildFileName({
        entityTitle: CONFIG.entityTitle,
        companyName,
        timestamp: today,
      });
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error("[DefaultReportView] PDF export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="mx-auto px-8 pt-16 pb-8" style={{ maxWidth: 1000 }}>
          <div className="flex items-center justify-end mb-6">
            <div className="h-8 w-28 rounded-lg bg-innerBg animate-pulse" />
          </div>
          <div className="flex flex-col gap-4">
            {Array.from({ length: 6 }, (_, i) => (
              <TileSkeleton key={i} delay={i * 60} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4 pt-14">
        <FileText
          className="w-8 h-8 text-zinc-300 dark:text-zinc-700"
          strokeWidth={1.5}
        />
        <p className="text-[13px] text-zinc-400 dark:text-zinc-600">
          Failed to load reports
        </p>
        <button
          onClick={loadReports}
          className="flex items-center gap-1.5 text-[12px] text-velBlue hover:underline"
        >
          <RefreshCw className="w-3 h-3" strokeWidth={2} />
          Try again
        </button>
      </div>
    );
  }

  /* ── Empty ── */
  if (reports.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 pt-14">
        <FileText
          className="w-8 h-8 text-zinc-300 dark:text-zinc-700"
          strokeWidth={1.5}
        />
        <p className="text-[14px] text-zinc-400 dark:text-zinc-600">
          No reports yet for {companyName || "this Rx brand name"}.
        </p>
      </div>
    );
  }

  /* ── Populated ── */
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto px-8 pt-16 pb-8" style={{ maxWidth: 1000 }}>
        {/* ── Top bar ── */}
        <div className="flex items-center justify-end mb-6">
          <button
            onClick={handleExportAll}
            disabled={isExporting}
            style={{
              background: t.isDark ? t.badgeBg : t.labelBg,
              border: `1px solid ${t.isDark ? t.badgeBorder : t.labelBorder}`,
              color: t.isDark ? t.badgeText : t.labelText,
            }}
            className="flex items-center gap-2 px-3 py-1.5 mt-4 rounded-lg text-[11px]
                       font-medium transition-opacity hover:opacity-70
                       active:opacity-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
            ) : (
              <FileDown className="h-3 w-3" strokeWidth={2} />
            )}
            {isExporting ? "Exporting…" : "Export PDF"}
          </button>
        </div>

        {/* ── Tiles ── */}
        <div className="flex flex-col gap-4">
          {reports.map((report, index) => (
            <div
              key={report.target_type || index}
              style={{
                transition: `opacity 0.4s ease-out ${Math.min(index * 55, 650)}ms, transform 0.4s ease-out ${Math.min(index * 55, 650)}ms`,
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(10px)",
              }}
            >
              <TileReport data={report} t={t} companyName={companyName} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DefaultReportView;
